import { io, Socket } from 'socket.io-client';
import { getServerUrl } from '../config';
import { fetchPiletaDevice, PiletaDevice, PiletaChipsState } from './piletaApi';
import { eventBus } from './EventBus';
import { canViewPileta, getUser } from './auth';

/** Mapa singleton (id → device). */
export const mapaPileta: Record<number, PiletaDevice> = {};
let loaded = false;
let socket: Socket | null = null;
let visibilityBound = false;
/** Un solo GET a la vez: foco + visibilidad + reconnect no se pisan. */
let refreshInFlight: Promise<PiletaDevice[]> | null = null;

/** Al volver del background móvil: HTTP + forzar socket (eventos MQTT perdidos). */
function syncPiletaWhenVisible() {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  if (!canViewPileta(getUser())) return;
  if (socket && !socket.connected) {
    try { socket.connect(); } catch { /* ignore */ }
  } else if (!socket) {
    ensureSocket();
  }
  void refrescarPileta();
}

function bindVisibilitySync() {
  if (visibilityBound || typeof document === 'undefined') return;
  visibilityBound = true;
  document.addEventListener('visibilitychange', syncPiletaWhenVisible);
  window.addEventListener('pageshow', syncPiletaWhenVisible);
  window.addEventListener('focus', syncPiletaWhenVisible);
}

export function isMapaPiletaCargado(): boolean {
  return loaded;
}

export function getPiletaByHabitacion(habitacionId: number): PiletaDevice[] {
  const user = getUser();
  // user null = localStorage aún no listo. No ocultar la tarjeta: la vista ya
  // filtró con userData. Solo un usuario cargado sin permiso la saca.
  if (user && !canViewPileta(user)) return [];
  const roomId = Number(habitacionId);
  if (!Number.isFinite(roomId)) return [];
  return Object.values(mapaPileta).filter((d) => {
    if (d.enabled === false) return false;
    return Number(d.habitacion_id) === roomId;
  });
}

function clearMapaPileta(): void {
  const ids = Object.keys(mapaPileta);
  if (ids.length === 0) return;
  ids.forEach((k) => delete mapaPileta[Number(k)]);
  eventBus.emit('pileta:updated');
}

function httpStatus(err: unknown): number | undefined {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

function merge(devices: PiletaDevice[]) {
  devices.forEach((d) => {
    if (!d?.id) return;
    mapaPileta[d.id] = { ...(mapaPileta[d.id] || {}), ...d };
  });
  eventBus.emit('pileta:updated');
}

export function upsertPiletaDevice(device: PiletaDevice): void {
  if (!device?.id) return;
  merge([device]);
}

export function patchPiletaDeviceLocal(device: PiletaDevice): void {
  upsertPiletaDevice(device);
}

async function refrescarPiletaNow(): Promise<PiletaDevice[]> {
  const user = getUser();
  if (!user) {
    // Sesión todavía no leída: conservar lo que ya se mostraba.
    loaded = true;
    return Object.values(mapaPileta);
  }
  if (!canViewPileta(user)) {
    clearMapaPileta();
    loaded = true;
    return [];
  }
  try {
    const device = await fetchPiletaDevice();
    if (!device?.id) {
      // Respuesta vacía: no borrar la tarjeta que ya está en la habitación.
      loaded = true;
      return Object.values(mapaPileta);
    }
    const prev = mapaPileta[device.id];
    const next: PiletaDevice = { ...(prev || {}), ...device };
    if ((device.habitacion_id === undefined || device.habitacion_id === null) && prev?.habitacion_id != null) {
      next.habitacion_id = prev.habitacion_id;
    }
    Object.keys(mapaPileta).forEach((k) => {
      if (Number(k) !== device.id) delete mapaPileta[Number(k)];
    });
    mapaPileta[device.id] = next;
    loaded = true;
    eventBus.emit('pileta:updated');
    return [next];
  } catch (err) {
    loaded = true;
    const status = httpStatus(err);
    if (status === 403 || status === 404) {
      clearMapaPileta();
      return [];
    }
    return Object.values(mapaPileta);
  }
}

export function refrescarPileta(): Promise<PiletaDevice[]> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refrescarPiletaNow().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export async function cargarPileta(): Promise<PiletaDevice[]> {
  const list = await refrescarPileta();
  if (canViewPileta(getUser())) {
    bindVisibilitySync();
    ensureSocket();
  }
  return list;
}

function ensureSocket() {
  if (socket) return;
  if (!canViewPileta(getUser())) return;
  const token = localStorage.getItem('token');
  if (!token) return;
  bindVisibilitySync();
  let serverUrl = getServerUrl();
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    serverUrl = serverUrl.replace(/^http:/, 'https:').replace(/^ws:/, 'wss:');
  }
  socket = io(serverUrl, {
    path: '/socket.io-pileta',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
  });
  // Tras reconnect: snapshot HTTP (el servidor también emite pileta_status al connect).
  socket.on('connect', () => {
    void refrescarPileta();
  });
  socket.on('pileta_status', (data: {
    online?: boolean;
    chips_state?: PiletaChipsState | null;
    updated_at?: string;
    error?: string;
  }) => {
    const ids = Object.keys(mapaPileta).map(Number);
    if (ids.length === 0) {
      void refrescarPileta();
      return;
    }
    ids.forEach((id) => {
      const existing = mapaPileta[id];
      if (!existing) return;
      const patch: PiletaDevice = { ...existing };
      if (data.online != null) patch.online = data.online;
      if (data.chips_state !== undefined) patch.chips_state = data.chips_state;
      merge([patch]);
    });
  });
}
